import { Component, Input, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeDefinitionValueListItem } from 'src/app/shared/models/attributes/attributeDefinitionValueListItem';
import { CustomFieldEditorModalComponent } from '../custom-field-editor-modal/custom-field-editor-modal.component';
import { FlatAttribute } from 'src/app/shared/models/attributes/flatAttribute';
import { IGenericAttributeService } from 'src/shared-app-portal/interfaces/i-generic-attribute-service';
import {
  ModalConfirmationComponent,
  ModalConfirmationModel
} from '../modal-confirmation/modal-confirmation.component';
import { MatDialog } from '@angular/material/dialog';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import {
  formatDays,
  getDurationDisplayFormat
} from 'src/app/shared/models/attributes/durationFormula';

@Component({
  selector: 'profile-custom-field-editor',
  templateUrl: './profile-custom-field-editor.component.html',
  styleUrls: ['./profile-custom-field-editor.component.scss'],
  standalone: false
})
export class ProfileCustomFieldEditorComponent {
  public flatAttribute: FlatAttribute;
  private _customFieldValues: Attribute[];
  private _customField: AttributeDefinition;

  @Input() readOnly: boolean = false;

  /**
   * Calculated fields are computed by the backend formula evaluator; their value is overwritten
   * on the next recalc. Treat them as read-only at the editor level so the lock icon shows and
   * the edit/add/delete affordances stay hidden — regardless of how the parent component sets
   * the explicit `readOnly` input.
   */
  get effectivelyReadOnly(): boolean {
    return this.readOnly || this._customField?.uiType === AttributeUiType.CalculatedField;
  }

  @Input()
  get customField(): AttributeDefinition {
    return this._customField;
  }
  set customField(value: AttributeDefinition) {
    this._customField = value;
    this.resetState();
  }
  @Input()
  get customFieldValues(): Attribute[] {
    return this._customFieldValues;
  }
  set customFieldValues(value: Attribute[]) {
    this._customFieldValues = value;
    this.resetState();
  }
  @Input() genericAttributeService: IGenericAttributeService;

  constructor(
    private datePipe: DatePipe,
    private dialog: MatDialog
  ) {}

  get firstElementValueListItem(): AttributeDefinitionValueListItem {
    return this._customFieldValues[0]?.attributeDefinitionValueListItem;
  }
  private resetState = () => {
    this.flatAttribute = this.customFieldValues?.length
      ? FlatAttribute.fromAttributes(this.customFieldValues)[0]
      : this.customField
        ? FlatAttribute.fromAttributeDefinition(this.customField)
        : null;
  };

  formatDisplayValue(attribute: Attribute): string {
    const durationDisplayFormat = getDurationDisplayFormat(this._customField);
    let displayValue = '';

    if (attribute && attribute.type === 'Date' && attribute.hasValue()) {
      displayValue = this.datePipe.transform(attribute.value, 'mediumDate');
    } else if (attribute?.hasValue() && durationDisplayFormat) {
      // duration calc fields store a raw signed-integer day count in `value`; the formula's
      // `displayFormat` decides whether the user sees the bare number or a Y/M/D breakdown.
      // Detected via getDurationDisplayFormat on `_customField` rather than on `attribute.type`
      // because calc-field-typed attributes report their materialized AttributeUIType
      // (Number/Text/etc.), not "CalculatedField".
      displayValue = formatDays(attribute.value, durationDisplayFormat);
    } else {
      displayValue = attribute.hasValue() ? attribute.value : '';
    }
    if (
      displayValue &&
      attribute?.otherValue &&
      (attribute.type === AttributeUiType.DropDown ||
        attribute.type === AttributeUiType.RadioButtonGroup ||
        attribute.type === AttributeUiType.MultiSelect ||
        attribute.type === AttributeUiType.Checkboxes)
    ) {
      displayValue = `${attribute.otherValue} (${displayValue})`;
    }
    return displayValue;
  }

  @ViewChild(CustomFieldEditorModalComponent, { static: true })
  editAccountModulesComponent: CustomFieldEditorModalComponent;

  openEditAttributeModal() {
    this.editAccountModulesComponent.open(this.customFieldValues, this.genericAttributeService);
  }

  public onClearField() {
    const deleteModel: ModalConfirmationModel = {
      simpleDialog: false,
      title: null,
      action: `delete ${this.customField.name}`,
      delete: true,
      okButtonText: 'Delete',
      okButtonId: 'modal-delete-credential',
      description: '',
      textMatcherValue: 'DELETE'
    };

    let ref = this.dialog
      .open(ModalConfirmationComponent, {
        data: deleteModel
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.genericAttributeService.deleteAttribute();
        }
      });
  }
}
